import { Injectable, signal, inject } from '@angular/core';
import { ParsedItem, PrescriptionDetail, Customer, Product } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';
import { DataService } from './data.service';

@Injectable()
export class GeminiService {
  private supabase: SupabaseClient | null = getSupabase();
  private dataService = inject(DataService);

  isConfigured = signal(isSupabaseConfigured());

  constructor() {}

  async parseOrder(prompt: string): Promise<ParsedItem[]> {
    const products = await this.dataService.getProducts();

    if (!this.isConfigured() || !this.supabase) {
      console.warn('Supabase not configured. Using local mock parser.');
      return this.mockParseOrder(prompt, products);
    }

    const productList = products.map(p => p.name);
    
    try {
      const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
        body: {
          type: 'parse_order',
          payload: { prompt, productList }
        }
      });

      if (error) throw error;
      
      const parsedResult = data;
      if (Array.isArray(parsedResult)) {
        return parsedResult.filter(
          item => typeof item === 'object' && item !== null && 'itemName' in item && 'quantity' in item
        );
      }
      return [];

    } catch (error: any) {
      console.warn('Gemini Edge Function failed. Falling back to local logic.', error);
      return this.mockParseOrder(prompt, products);
    }
  }

  async generatePrescriptionDetails(prompt: string): Promise<PrescriptionDetail> {
    if (!this.isConfigured() || !this.supabase) {
       return this.mockPrescription(prompt);
    }

    try {
      const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
        body: {
          type: 'generate_prescription',
          payload: { prompt }
        }
      });

      if (error) throw error;
      return data as PrescriptionDetail;

    } catch (error) {
      console.warn('Gemini Edge Function failed.', error);
      return this.mockPrescription(prompt);
    }
  }

  async extractThaiIdCard(imageBase64: string): Promise<Partial<Customer>> {
    if (!this.isConfigured() || !this.supabase) {
        return this.mockOCR();
    }

    // HOTFIX: The previous "softened" instruction caused ambiguity on cards with both Thai and English names.
    // This new instruction is more explicit, commanding the AI to ALWAYS use the Thai name, resolving the regression.
    const systemInstruction = `You are an AI assistant for a Thai medical clinic's KYC process. Your goal is accuracy.
    
    Instructions:
    1.  **Extract Thai Name:** The 'fullName' field MUST be the Thai name written on the card (e.g., 'นาย/นาง/น.ส. ...'). Ignore any English name present.
    2.  **Citizen ID:** The 'citizenId' MUST be exactly 13 digits. Return only digits, no hyphens.
    3.  **Date of Birth:** The 'birthDate' MUST be in YYYY-MM-DD format. Convert the Buddhist year (พ.ศ.) to Christian year (A.D.). For example, 2536 becomes 1993.
    4.  **Data Integrity:** If a field is completely unreadable, return null for that specific field. Do not guess critical information.
    5.  **Quality Check:** Assess the image quality and provide a reason if it fails.`;

    try {
      const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
        body: {
          type: 'extract_id_card',
          payload: { 
            image: imageBase64,
            systemInstruction: systemInstruction 
          }
        }
      });

      if (error) throw error;

      if (data.qualityCheck && data.qualityCheck !== 'PASS') {
          let msg = 'ไม่สามารถอ่านข้อมูลได้';
          switch(data.qualityCheck) {
              case 'FAIL_BLURRY':
                  msg = 'ภาพไม่ชัด/เบลอ กรุณาถือนิ่งๆ และถ่ายในที่สว่าง';
                  break;
              case 'FAIL_GLARE':
                  msg = 'มีแสงสะท้อนบนบัตร กรุณาเอียงบัตรหลบแสง';
                  break;
              case 'FAIL_CROP':
                  msg = 'เห็นบัตรไม่ครบใบ กรุณาวางบัตรให้เต็มกรอบ';
                  break;
              case 'FAIL_NOT_ID':
                  msg = 'ไม่พบรูปบัตรประชาชนในภาพ';
                  break;
          }
          throw new Error(msg);
      }

      let cleanCitizenId = data.citizenId ? String(data.citizenId).replace(/\D/g, '') : null;

      if (!cleanCitizenId || cleanCitizenId.length !== 13) {
          throw new Error("อ่านเลขบัตรไม่ครบ 13 หลัก (ภาพอาจไม่ชัด)");
      }
      if (!data.fullName) {
          throw new Error("ไม่สามารถอ่านชื่อ-นามสกุลได้");
      }

      return {
          fullName: data.fullName,
          citizenId: cleanCitizenId,
          birthDate: data.birthDate,
          address: data.address
      } as Partial<Customer>;

    } catch (error) {
      console.warn('OCR processing error:', error);
      throw error;
    }
  }

  // UPDATED: Now accepts optional 'features' array
  async generateMarketingContent(topic: string, format: string, tone: string, features?: string[]): Promise<string> {
    if (!this.isConfigured() || !this.supabase) {
        return "Mock Content: Please configure Supabase and Gemini API Key to generate real content.";
    }
    const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
      body: {
        type: 'marketing_content',
        payload: { topic, format, tone, features }
      }
    });
    if (error) throw error;
    return data.content;
  }

  // UPDATED: Now accepts optional 'features' array
  async generateMarketingStrategy(goal: string, audience: string, features?: string[]): Promise<string> {
    if (!this.isConfigured() || !this.supabase) {
        return "Mock Strategy: 1. Target Local Ads, 2. Promotion: Buy 1 Get 1, 3. Channel: Facebook.";
    }
    const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
      body: {
        type: 'marketing_strategy',
        payload: { goal, audience, features }
      }
    });
    if (error) throw error;
    return data.content;
  }
  
  // UPDATED: Now accepts optional 'features' array
  async generateVideoScript(topic: string, duration: string, features?: string[]): Promise<string> {
     if (!this.isConfigured() || !this.supabase) {
        return "Mock Script: [00-05s] Visual: Intro... Audio: Hello...";
    }
    const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
      body: {
        type: 'marketing_video_script',
        payload: { topic, duration, features }
      }
    });
    if (error) throw error;
    return data.content;
  }

  async generateMarketingImage(prompt: string): Promise<string> {
     if (!this.isConfigured() || !this.supabase) {
        return "https://picsum.photos/800/450";
    }
    const { data, error } = await this.supabase.functions.invoke('invoke_gemini', {
      body: {
        type: 'marketing_image',
        payload: { prompt }
      }
    });
    if (error) throw error;
    return data.imageUrl;
  }

  // --- LOCAL FALLBACK LOGIC ---

  private mockParseOrder(prompt: string, products: Product[]): ParsedItem[] {
    const result: ParsedItem[] = [];
    const lowerPrompt = prompt.toLowerCase();
    
    for (const p of products) {
      if (lowerPrompt.includes(p.name.toLowerCase())) {
        const quantity = 1; 
        result.push({ itemName: p.name, quantity });
      }
    }
    
    if (result.length === 0 && products.length > 0) {
       result.push({ itemName: products[0].name, quantity: 1 });
    }

    return result;
  }

  private mockPrescription(prompt: string): Promise<PrescriptionDetail> {
    return new Promise(resolve => setTimeout(() => resolve({
      symptoms: prompt || 'Insomnia, Stress',
      dosagePerDay: 1,
      dosageUnit: 'GRAM',
      durationDays: 30,
      totalAmount: 30,
      usageInstructions: 'Inhale before bedtime for relaxation.',
      productName: 'Tiger Eye (Indica)'
    }), 1500));
  }

  private mockOCR(): Promise<Partial<Customer>> {
    return new Promise(resolve => setTimeout(() => resolve({
        fullName: 'นาย สมชาย ตัวอย่าง (AI Mock)',
        citizenId: '1-1234-56789-00-1',
        birthDate: '1990-01-01',
        address: '123 ถ.ตัวอย่าง แขวงจำลอง เขตทดสอบ กทม 10110'
    }), 1500));
  }
}